import React, { useEffect, useMemo, useRef, useState } from 'react';
import { holdScrollPosition } from '../utils/holdScrollPosition';
import './PeriodSelector.css';

const DEFAULT_OPTIONS = [30, 15, 7];

// Période mémorisée sur l'appareil, si elle fait encore partie des options : une valeur
// retirée depuis (180 j, illisible sur les graphiques) retombe sur `fallback`.
export function readStoredPeriod(storageKey, fallback, options = DEFAULT_OPTIONS) {
  if (!storageKey) return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    const n = raw ? Number(raw) : NaN;
    return options.includes(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function storePeriod(storageKey, value) {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(storageKey, String(value));
  } catch {
    return;
  }
}

const PeriodSelector = ({
  value,
  onChange,
  options = DEFAULT_OPTIONS,
  storageKey,
  className,
  arrowClassName,
  menuAlign = 'right',
}) => {
  const normalizedOptions = useMemo(
    () => [...new Set(options)].filter((n) => Number.isFinite(n)).sort((a, b) => b - a),
    [options]
  );

  const initialValue = value ?? readStoredPeriod(storageKey, normalizedOptions[0] ?? 30, normalizedOptions);
  const [internalValue, setInternalValue] = useState(initialValue);
  const selected = value ?? internalValue;

  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleSelect = (n) => {
    // Une autre période change la longueur des listes sous le sélecteur : même saut en
    // bas de page que la bascule liste / groupé (cf. holdScrollPosition).
    if (n !== selected) holdScrollPosition(rootRef.current);
    if (onChange) onChange(n);
    setInternalValue(n);
    storePeriod(storageKey, n);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`period-selector${open ? ' period-selector--open' : ''}`}>
      <button
        type="button"
        className={`period-selector__button${className ? ` ${className}` : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected}j <span className={arrowClassName ?? 'period-selector__arrow'}>&#8964;</span>
      </button>

      {open && (
        <div
          className={`period-selector__menu period-selector__menu--${menuAlign}`}
          role="listbox"
          aria-label="Sélection de période"
        >
          {normalizedOptions.map((n) => (
            <button
              key={n}
              type="button"
              className={`period-selector__option${n === selected ? ' period-selector__option--active' : ''}`}
              onClick={() => handleSelect(n)}
              role="option"
              aria-selected={n === selected}
            >
              {n} jours
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PeriodSelector;
